import { useEffect, useMemo, useState } from "react";
import { Spinner } from "@/components/ui/Spinner";
import { BillingAnsweredRowCard } from "@/features/timesheetFlow/components/BillingAnsweredRowCard";
import { BillingAnsweredRowsDialog } from "@/features/timesheetFlow/components/BillingAnsweredRowsDialog";
import { resolveAnsweredArticleDisplay } from "@/features/timesheetFlow/billingAnswerPositionUtils";
import {
  flattenBillingQuestionsToAnswerRows,
} from "@/features/timesheetFlow/billingQuestionAnswerRows";
import { formatBillingMaterialLine } from "@/features/timesheetFlow/timesheetBillingUtils";
import {
  getActionLog,
  getTimesheetDetail,
  listBillingQuestions,
  type ActionLogEntry,
  type BillingQuestion,
} from "@/features/timesheetFlow/timesheetFlowApi";
import type { TimesheetMaterialRecord } from "@/features/timesheetFlow/timesheetFlowTypes";
import { useCurrentUser, userCanSeePrices } from "@/lib/auth";
import type { TimesheetSummary } from "@/lib/timesheets";
import { TimesheetBadge } from "@/pages/stundennachweise/TimesheetBadge";
import {
  getTimesheetDisplayNumber,
  getTimesheetStatus,
  statusBadgeClass,
  timesheetStatusLabel,
} from "@/pages/stundennachweise/timesheetFilterLogic";

/** Eigenständiges Modal: bisher gegebene Antworten inkl. Stundennachweis-Status. */
export function BillingAnswersPreviewDialog({
  open,
  onClose,
  timesheet,
}: {
  open: boolean;
  onClose: () => void;
  timesheet: TimesheetSummary | null;
}) {
  const { user } = useCurrentUser();
  const canSeePrices = userCanSeePrices(user);
  const timesheetId = timesheet?.id ?? null;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [questions, setQuestions] = useState<BillingQuestion[]>([]);
  const [materials, setMaterials] = useState<TimesheetMaterialRecord[]>([]);
  const [actionLog, setActionLog] = useState<ActionLogEntry[]>([]);

  useEffect(() => {
    if (!open || timesheetId == null) {
      setQuestions([]);
      setMaterials([]);
      setActionLog([]);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    void Promise.all([
      listBillingQuestions(timesheetId, { includeAnswered: true }),
      getTimesheetDetail(timesheetId).catch(() => null),
      getActionLog(timesheetId).catch(() => []),
    ])
      .then(([list, detail, log]) => {
        if (cancelled) return;
        setQuestions(list);
        setMaterials(detail?.materials ?? []);
        setActionLog(log);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Antworten konnten nicht geladen werden.");
        setQuestions([]);
        setMaterials([]);
        setActionLog([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, timesheetId]);

  const answeredRows = useMemo(
    () => flattenBillingQuestionsToAnswerRows(questions, materials).filter((row) => !row.isOpen),
    [questions, materials],
  );

  if (!timesheet) return null;

  const status = getTimesheetStatus(timesheet);
  const displayNumber = getTimesheetDisplayNumber(timesheet) || String(timesheet.id);

  return (
    <BillingAnsweredRowsDialog
      open={open}
      onClose={onClose}
      count={answeredRows.length}
      statusBadge={
        <TimesheetBadge className={statusBadgeClass(status, user)}>
          {timesheetStatusLabel(status)}
        </TimesheetBadge>
      }
      description={`Bisher gegebene Antworten zu Stundennachweis ${displayNumber}.`}
    >
      {loading ? (
        <div className="col-span-full flex justify-center py-10">
          <Spinner />
        </div>
      ) : error ? (
        <p className="col-span-full text-sm text-status-red">{error}</p>
      ) : answeredRows.length === 0 ? (
        <p className="col-span-full text-sm text-label-secondary">
          Noch keine beantworteten Rückfragen.
        </p>
      ) : (
        answeredRows.map((row) => {
          const articleDisplay = resolveAnsweredArticleDisplay({
            materialPositionId: row.materialPositionId,
            answerText: row.answerText,
            respondedAt: row.respondedAt,
            materials,
            actionLog,
            showPrices: canSeePrices,
            formatMaterialLine: (material) =>
              formatBillingMaterialLine(material as TimesheetMaterialRecord).line || null,
          });
          return (
            <BillingAnsweredRowCard
              key={row.key}
              row={row}
              articleLines={articleDisplay.lines}
              unterpositionCount={articleDisplay.unterpositionCount}
            />
          );
        })
      )}
    </BillingAnsweredRowsDialog>
  );
}
